import React, { CSSProperties } from "react"
import { differenceInDays, parse } from "date-fns"
import { IntlShape } from "react-intl"
import {
	FORMATS,
	STORE,
	VENDORS,
	LOCATION_GROUP_FILTER,
	PRODUCT_GROUP_FILTER,
} from "components/Shared/GlobalFilter/constants"
import { FilterTypes } from "components/Shared/GlobalFilter/types"
import { FiltersType } from "components/Shared/GlobalFilter/context/interfaces"
import { Tuple } from "components/Shared/Interfaces"
import {
	RequestParameters,
	OptionsSelectDate,
	LevelName,
	OptionsFiltersSelect,
	BenchmarkPeriod,
	BenchmarkTimelineData,
	BenchmarkLineChart,
	BenchmarkSummaryData,
	FilterLevelSelected,
} from "../interfaces"
import {
	COLUMN_NAMES,
	CURRENT_PERIOD,
	DEFAULT_SUBTITLE_ID,
	ERROR_CODE_TIMEOUT,
	LabelTranslation,
	legendsWithClassAndPeriod,
	LEVEL_NAME_DEFAULT,
	optionsLegendTable,
	PREVIOUS_PERIOD,
	STATUS_CODE_TIMEOUT,
	subTitleDeparment,
	TRANSLATION_PRESETS_RANGES,
	variationValues,
	WALMART,
	DEPARTMENT_MARKET_TITLE_MULTIPLE,
	CATEGORIES_MARKET_TITLE_MULTIPLE,
	SUB_CATEGORIES_MARKET_TITLE_MULTIPLE,
	METRICS,
	PRESET,
} from "../constants"
import { handleFilters } from "components/Shared/NestedCategoryFilters/utils/utils"
import { LABEL_DEFAULT_FILTER_SELECT } from "components/CustomerBehavior/Switching/constants"
import { resolveMagnitude } from "../../../../helpers/numbers"
import {
	DAILY_INTERVAL,
	MONTHLY_INTERVAL,
	WEEKLY_INTERVAL,
} from "components/Shared/Charts/Timeline/constant"
import { capitalize, dashesToUnderscore } from "../../../../helpers/text"
import { useApplicationContext } from "context/applicationContext"
import {
	CURRENT_DAYS,
	CURRENT_MONTH,
	CURRENT_QUARTER,
	CURRENT_YEAR,
} from "helpers/temporal"
import { ColumnsHeader } from "components/Shared/InformationTable/interfaces"
import { isNotEmpty } from "helpers/objects"
import { COLOR_PALETTE } from "colors"

const METRIC_PHRASE =
	"phronesis.transactional.application.commercial.benchmark.metric.phrase"

export const createOptionsSelectDate = (
	options: Array<OptionsSelectDate>,
	intl: IntlShape
) =>
	options.map(({ code, id }) => ({
		code: code,
		name: intl.formatMessage({ id }),
	}))

export const getVendorsForUserOrganizationString = (vendors: Tuple[]) => {
	return vendors.map((tuple) => tuple.code).join(",")
}

export const setVendorForTheFirstTime = (
	setFilters: (arg0: any) => void,
	setFiltersBenchmark: (arg0: any) => void,
	setApplyFiltersAction: (arg0: boolean) => void,
	vendors: Tuple[],
	globalFilters: FiltersType,
	filters: RequestParameters
) => {
	setApplyFiltersAction(false)
	if (vendors.length > 0) {
		if (globalFilters.vendors.length === 0) {
			handleFilters(VENDORS, vendors, setFilters, globalFilters)
			setFiltersBenchmark({
				...filters,
				vendors: getVendorsForUserOrganizationString(vendors),
			})
		} else {
			setFiltersBenchmark({
				...filters,
				region: globalFilters.regions.map((tuple) => tuple.code).join(","),
				county: globalFilters.counties.map((tuple) => tuple.code).join(","),
				store: globalFilters.stores.map((tuple) => tuple.code).join(","),
				vendors: globalFilters.vendors.map((tuple) => tuple.code).join(","),
			})
		}
	}
}

export const validateIsCodeOrStatusTimeout = (
	codeError: string,
	status: string
) => {
	return codeError === ERROR_CODE_TIMEOUT || status === STATUS_CODE_TIMEOUT
}

export const getCodeAndStatusError = (error: any) => {
	if (error && error.response) {
		const { errCode } = error.response.data
		const { status } = error.response
		return { errCode, status }
	} else {
		return { errCode: "", status: "" }
	}
}

export const errorBenchmarkStub = (
	errCode: string,
	status: number
): {
	toJSON: () => ObjectConstructor
	response: {
		headers: {}
		data: { errCode: string }
		statusText: string
		config: {}
		status: number
	}
	name: string
	message: string
	isAxiosError: boolean
	config: {}
} => {
	return {
		response: {
			data: {
				errCode: errCode,
			},
			status: status,
			statusText: "",
			headers: {},
			config: {},
		},
		isAxiosError: true,
		config: {},
		toJSON: () => Object,
		name: "",
		message: "",
	}
}
export const filtersToShow: Array<FilterTypes> = [
	LOCATION_GROUP_FILTER,
	PRODUCT_GROUP_FILTER,
]

export const generateInsightTitle = (
	titleId: string,
	levelName: LevelName,
	labelLevelNameFather: string | undefined,
	intl: IntlShape,
	metric?: string | undefined
) => {
	const formatMessage = intl.formatMessage(
		{ id: titleId },
		{
			metric: intl.formatMessage(
				{
					id: METRIC_PHRASE,
				},
				{ metric: dashesToUnderscore(metric) }
			),
			filter: generateCenterPhrase(levelName, labelLevelNameFather, intl),
		}
	)

	return formatMessage.charAt(0).toUpperCase() + formatMessage.slice(1)
}

export const formatInsightSubtitle = (
	filter: LevelName,
	labelLevelNameFather: string | undefined,
	period: string | undefined,
	intl: IntlShape,
	metric?: string | undefined,
	interval?: string | undefined
) => {
	const metricStr = intl.formatMessage(
		{
			id: METRIC_PHRASE,
		},
		{ metric: dashesToUnderscore(metric) }
	)

	return intl.formatMessage(
		{ id: getBenchmarkInsightSubtitleId(metric) },
		{
			metric: metricStr,
			interval: intl.formatMessage(
				{ id: getIntervalTranslate(interval, metric) },
				{
					metric: metricStr,
				}
			),
			filter: generateCenterPhrase(filter, labelLevelNameFather, intl),
			period: intl.formatMessage({ id: getPeriodTranslate(period) }),
			b: (str: string) => <b>{str}</b>,
		}
	)
}

export const getNameToPeriodLegend = (period: string) => {
	const periodLegend = optionsLegendTable.find((item) => item.code === period)
	return periodLegend ? periodLegend : optionsLegendTable[0]
}

export const generateCenterPhrase = (
	levelName: LevelName,
	labelLevelNameFather: string | undefined,
	intl: IntlShape
) => {
	return levelName === LEVEL_NAME_DEFAULT
		? intl.formatMessage({ id: subTitleDeparment.id })
		: labelLevelNameFather
}

export const renderOperatorAndClassName = (variation: number) => {
	const resultVariation = Math.sign(variation)
	return variationValues.find(
		(variation) => variation.resultVariation === resultVariation
	)
}

export const validateIsPreviousPeriod = (period: string) =>
	period === PREVIOUS_PERIOD

export const validateLocationFilters = (
	filtersBenchmark: any,
	metricName: string = ""
) => {
	const { region, county } = filtersBenchmark
	const isRegion = Boolean(region)
	const isCounty = Boolean(county)
	const isLocation = metricName.includes("by_location")
	return isRegion && isCounty && isLocation
}

export const configurateColumnsHeaderBenchmark = (
	levelName: LevelName,
	filtersBenchmark: any,
	intl: IntlShape,
	isPenetrationMetric: boolean,
	metricName: string
): Array<ColumnsHeader> => {
	const levelNameProcess = getLevelName(filtersBenchmark, levelName)
	const showIdStoreHeader = validateLocationFilters(
		filtersBenchmark,
		metricName
	)
	let result = []
	const titleDifferenceColumn = isPenetrationMetric ? "variation" : "growth"
	const firtsHeader = {
		name: intl.formatMessage({
			id: `phronesis.transactional.application.commercial.benchmark.title.first.column.${levelNameProcess}`,
		}),
		alignment: "left",
		nameColumnSort: "name",
		hasSort: true,
		className: "first-header-column",
	}
	const storeId = {
		name: intl.formatMessage({
			id:
				"phronesis.transactional.application.commercial.benchmark.title.column.idstore",
		}),
		alignment: "left",
		nameColumnSort: "idstore",
		hasSort: true,
		className: "label-header-column",
	}
	const labelHeader = {
		name: intl.formatMessage({
			id: `phronesis.transactional.application.commercial.benchmark.title.column.${titleDifferenceColumn}`,
		}),
		alignment: "right",
		nameColumnSort: "growthPercentage",
		hasSort: true,
		className: "label-header-column",
	}
	const barcodeHeader = {
		name: intl.formatMessage({
			id: `phronesis.transactional.application.commercial.benchmark.title.column.barcode`,
		}),
		alignment: "left",
		nameColumnSort: "barcode",
		hasSort: true,
		className: "label-header-column",
	}

	if (!isPenetrationMetric) {
		result = [
			firtsHeader,
			storeId,
			barcodeHeader,
			{
				name: intl.formatMessage(
					{
						id: METRIC_PHRASE,
					},
					{
						metric: dashesToUnderscore(filtersBenchmark.metricName),
					}
				),
				alignment: "left",
				nameColumnSort: "currentSales",
				hasSort: true,
				className: "second-header-column",
			},

			labelHeader,
			{
				name: intl.formatMessage({
					id:
						"phronesis.transactional.application.commercial.benchmark.title.column.growthWalmart",
				}),
				alignment: "right",
				nameColumnSort: "levelGrowthPercentage",
				hasSort: true,
				className: "four-header-column",
			},
		]
	} else {
		result = [
			firtsHeader,
			storeId,
			barcodeHeader,
			{
				name: intl.formatMessage({
					id:
						"phronesis.transactional.application.commercial.benchmark.title.column.currentPeriodPenetration",
				}),
				alignment: "right",
				nameColumnSort: "currentSales",
				hasSort: true,
				className: "second-header-column",
			},
			{
				name: intl.formatMessage({
					id:
						"phronesis.transactional.application.commercial.benchmark.title.column.previousPeriodPenetration",
				}),
				alignment: "right",
				nameColumnSort: "currentSales",
				hasSort: true,
				className: "three-header-column",
			},
			labelHeader,
		]
	}
	return cleanHeaders(levelNameProcess, result, showIdStoreHeader)
}

export const cleanHeaders = (
	levelName: LevelName,
	result: Array<ColumnsHeader>,
	showIdStoreHeader: boolean
) => {
	if (levelName != "item") delete result[2]
	if (!showIdStoreHeader) delete result[1]
	return result
}

const mustShowStore = (counties: string, stores: string | undefined) => {
	return Boolean(counties) || Boolean(stores)
}

export const getLocationLevel = (
	regions: string,
	counties: string,
	stores?: string
) => {
	if (mustShowStore(counties, stores)) {
		return COLUMN_NAMES.STORE
	} else if (Boolean(regions) === true) {
		return COLUMN_NAMES.COUNTY
	}
	return COLUMN_NAMES.REGION
}

export const getBrandLevel = (filters: any) => {
	if (filters["brands"]) return COLUMN_NAMES.ITEM
	return COLUMN_NAMES.BRAND
}

export const barColors: {
	[key: string]: NonNullable<CSSProperties["color"]>
} = {
	[PREVIOUS_PERIOD]: COLOR_PALETTE.YELLOW_600,
	[CURRENT_PERIOD]: COLOR_PALETTE.PURPLE_500,
}

export const getBarColor = (code: keyof typeof barColors) => {
	const color = barColors[code]

	if (!color) {
		throw new TypeError(`code ${code} not found`)
	}

	return color
}

export const getBarCellMax = (data: any) => {
	let valueMax = 0

	data.map((row: any) => {
		valueMax = Math.max(valueMax, row.previousSales, row.currentSales)
	})

	return valueMax
}

export const getChartMax = (data: any): number => {
	let valueMax = 0

	data?.data?.map((row: BenchmarkTimelineData) => {
		valueMax = Math.max(valueMax, row.previousAmount, row.currentAmount)
	})

	return valueMax
}

export const getSales = (row: any) => {
	return parseFloat(row.previousSales) + parseFloat(row.currentSales)
}

export const generateIdTooltip = (id: number) => {
	return `ID-${id.toString()}`
}

export const validateIsOptionAll = (labelSelect: string) => {
	return LABEL_DEFAULT_FILTER_SELECT.includes(labelSelect)
}

export const getFinalLabelSubTitle = (
	levelName?: LevelName,
	labelOption?: string,
	optionsFiltersSelect: OptionsFiltersSelect = {}
) => {
	if (levelName === "category") {
		return optionsFiltersSelect.departments
	} else if (levelName === "subcategory") {
		return optionsFiltersSelect.categories
	} else {
		return labelOption
	}
}

export const getTabTitle = (
	textId: string,
	levelName: string,
	intl: IntlShape,
	filtersBenchmark?: any,
	tabs?: string
) => {
	let textIdNew: string = textId
	const tabsTitle: Record<string, any> = {
		[METRICS.BY_BRAND]: getTabTitleBrands(filtersBenchmark, textId),
		[METRICS.BY_LOCATION]: getTabTitleLocation(filtersBenchmark, textId),
	}
	if (tabs) {
		textIdNew = tabsTitle[tabs]
	}
	return intl.formatMessage(
		{ id: textIdNew },
		{
			levelName: intl
				.formatMessage({
					id: getLevelNameTranslate(levelName)?.[1] || "phronesis.blank.space",
				})
				.toLocaleLowerCase(),
		}
	)
}

const getTabTitleLocation = (filtersBenchmark: any, textId: string) => {
	if (filtersBenchmark?.region)
		return "phronesis.transactional.application.commercial.benchmark.by.location.tab.title.county"
	if (filtersBenchmark?.county)
		return "phronesis.transactional.application.commercial.benchmark.by.location.tab.title.store"
	return textId
}

const getTabTitleBrands = (filtersBenchmark: any, textId: string) => {
	if (filtersBenchmark?.brands)
		return "phronesis.transactional.application.commercial.benchmark.by.item.tab.title"
	return textId
}

const getLevelNameTranslate = (levelName: string) => {
	const levels = {
		department:
			"phronesis.performance.application.switching.filters.departments.title",
		category:
			"phronesis.performance.application.switching.filters.categories.title",
		subcategory:
			"phronesis.performance.application.switching.filters.subcategories.title",
		fineline:
			"phronesis.performance.application.switching.filters.fineline.title",
		item: "phronesis.performance.application.switching.filters.item.title",
	}
	return Object.entries(levels).filter(
		([key, value]) => key === levelName ?? value
	)[0]
}

export const BENCHMARK_CHART_LEGEND =
	"phronesis.transactional.application.commercial.benchmark.timeline.footer.legend"

const getBenchmarkInsightSubtitleId = (metric: string | undefined) => {
	const { locale } = useApplicationContext()
	return LabelTranslation[`${metric}_${locale}`] || DEFAULT_SUBTITLE_ID
}

export const mapTimeInterval = (
	intervals: BenchmarkPeriod,
	period: string,
	intl: IntlShape
) => {
	if (period.includes(CURRENT_PERIOD)) {
		return intl.formatMessage(
			{ id: BENCHMARK_CHART_LEGEND },
			{
				startPeriod: intervals?.startCurrentPeriod,
				endPeriod: intervals?.endCurrentPeriod,
			}
		)
	}
	return intl.formatMessage(
		{ id: BENCHMARK_CHART_LEGEND },
		{
			startPeriod: intervals?.startPreviousPeriod,
			endPeriod: intervals?.endPreviousPeriod,
		}
	)
}

const getIntervalTranslate = (
	interval: string | undefined,
	metric: string | undefined
) => {
	return interval && metric
		? `phronesis.transactional.application.commercial.benchmark.insight.subtitle.${interval}.${metric}`
		: "phronesis.blank.space"
}

const getPeriodTranslate = (period: string | undefined) => {
	return period?.includes(PRESET)
		? `phronesis.transactional.application.commercial.benchmark.insight.subtitle.${period.toLowerCase()}`
		: "phronesis.blank.space"
}

export const formatXAxis = (interval: string, intl: IntlShape) => {
	return intl.formatMessage({
		id: `phronesis.transactional.application.commercial.benchmark.timeline.xAxis.format.${interval.toLocaleLowerCase()}`,
	})
}

export const formatYAxisLegend = (
	data: any,
	metric: string,
	intl: IntlShape,
	isPenetrationMetric: boolean
) => {
	const metricName = isPenetrationMetric ? "penetration" : metric
	const magnitude = isPenetrationMetric
		? intl.formatMessage({
				id:
					"phronesis.transactional.application.commercial.benchmark.timeline.yAxis.magnitude",
		  })
		: resolveMagnitude(getChartMax(data)).abbreviation
	return intl.formatMessage(
		{
			id:
				"phronesis.transactional.application.commercial.benchmark.timeline.yAxis.legend",
		},
		{
			metric: intl.formatMessage(
				{
					id: METRIC_PHRASE,
				},
				{ metric: metricName }
			),
			magnitude: magnitude,
		}
	)
}

export const mapTimeLineResponseForChart = (
	chartData: BenchmarkLineChart,
	intl: IntlShape
) => ({
	data: [
		mapDataForLineChart(chartData, intl, false),
		mapDataForLineChart(chartData, intl, true),
	],
})

const getIdKeyForTimeLine = (interval: string, isCurrentPeriod: boolean) => {
	return interval + (isCurrentPeriod ? CURRENT_PERIOD : PREVIOUS_PERIOD)
}

const mapDataForLineChart = (
	chartData: BenchmarkLineChart,
	intl: IntlShape,
	currentPeriod: boolean
) => {
	return {
		id: getIdKeyForTimeLine(chartData?.interval, currentPeriod),
		footer: mapTimeInterval(
			chartData?.periods,
			currentPeriod ? CURRENT_PERIOD : PREVIOUS_PERIOD,
			intl
		),
		data: chartData?.data?.map((timeLineData: BenchmarkTimelineData) => ({
			x: timeLineData.interval,
			y: currentPeriod
				? timeLineData.currentAmount
				: timeLineData.previousAmount,
			info: timeLineData,
		})),
	}
}
export const getMetricName = (
	metricName: string,
	period: string,
	intl: IntlShape,
	isOrdered: boolean
) => {
	const detail = metricPeriodDetail(metricName, isOrdered)
	return detail.isMain
		? intl.formatMessage({
				id: `phronesis.transactional.application.commercial.benchmark.legend.${detail.firstWord.toLowerCase()}${capitalize(
					period.split("_")[0]
				)}`,
		  })
		: intl.formatMessage({
				id: getGrowthDetail("title.column.growth", detail.secondWord),
		  })
}

const getPeriodsInOrder = (periods: BenchmarkPeriod, isOrdered: boolean) => {
	return isOrdered
		? {
				startCurrentPeriod: periods.startPreviousPeriod,
				startPreviousPeriod: periods.startCurrentPeriod,
				endCurrentPeriod: periods.endPreviousPeriod,
				endPreviousPeriod: periods.endCurrentPeriod,
		  }
		: periods
}

export const getMetricTooltip = (
	periodFilterName: string,
	periods: BenchmarkPeriod,
	metric: string | undefined,
	intl: IntlShape,
	isOrdered: boolean
) => {
	const detail = metricPeriodDetail(periodFilterName, isOrdered)
	const tooltipPeriods = getPeriodsInOrder(periods, isOrdered)

	return detail.isMain
		? intl.formatMessage(
				{
					id:
						"phronesis.transactional.application.commercial.benchmark.tooltip.periodBy",
				},
				{
					b: (str: string) => <b>{str}</b>,
					start_period: getMetricPeriodRange(
						detail.firstWord,
						tooltipPeriods.startCurrentPeriod,
						tooltipPeriods.startPreviousPeriod
					),
					end_period: getMetricPeriodRange(
						detail.firstWord,
						tooltipPeriods.endCurrentPeriod,
						tooltipPeriods.endPreviousPeriod
					),
				}
		  )
		: intl.formatMessage(
				{
					id: getGrowthDetail("tooltip.growth", detail.secondWord),
				},
				{
					metric: intl.formatMessage(
						{
							id: METRIC_PHRASE,
						},
						{ metric: metric?.split("_")[0] }
					),
				}
		  )
}

const getGrowthDetail = (pathTraslate: string, word: string) => {
	return `phronesis.transactional.application.commercial.benchmark.${pathTraslate}${
		word === WALMART ? capitalize(word) : ""
	}`
}

const metricPeriodDetail = (temporality: string, isOrdered: boolean) => {
	const metricDetail = temporality.split("_")
	const firstPeriod = isOrdered ? 0 : 1
	const secondPeriod = isOrdered ? 1 : 0
	return {
		isMain: [
			legendsWithClassAndPeriod[firstPeriod].period.toUpperCase(),
			legendsWithClassAndPeriod[secondPeriod].period.toUpperCase(),
		].includes(metricDetail[0]),
		firstWord: metricDetail[0],
		secondWord: metricDetail[1],
	}
}

const getMetricPeriodRange = (period: string, start: string, end: string) => {
	return period === legendsWithClassAndPeriod[1].period.toUpperCase()
		? start
		: end
}

export const metricWithColor = (percentage: number) => {
	return percentage !== null
}

export const mapResponseMetricSummary = (
	summaryData: Array<BenchmarkSummaryData>,
	periodData: BenchmarkPeriod,
	metric: string | undefined,
	intl: IntlShape,
	period: string,
	isOrdered: boolean
) => {
	return {
		data: summaryData?.map((summary: BenchmarkSummaryData) => ({
			header: {
				title: getMetricName(summary.name, period, intl, isOrdered),
				tooltip: getMetricTooltip(
					summary.name,
					periodData,
					metric,
					intl,
					isOrdered
				) as string,
			},
			content: {
				metric: summary.amount,
				percentage: summary.percentage,
				withColor: metricWithColor(summary.percentage),
			},
			footer: {
				details: [
					{
						detailName: intl.formatMessage({ id: "phronesis.common.stores" }),
						detailValue: summary.store,
					},
					{
						detailName: "E-commerce",
						detailValue: summary.ecommerce,
					},
				],
			},
		})),
	}
}

const parseDate = (date: string) => parse(date, "dd/MM/yyyy", new Date())

const getDifferentRange = (start: string, end: string) => {
	const startDate = parseDate(start)
	const endDate = parseDate(end)
	return differenceInDays(endDate, startDate)
}

const defaultInterval = { period: CURRENT_DAYS, interval: DAILY_INTERVAL }

const limitCustomRanges: {
	[key: number]: { period: string; interval: string }
} = {
	90: { period: CURRENT_YEAR, interval: MONTHLY_INTERVAL },
	31: { period: CURRENT_QUARTER, interval: MONTHLY_INTERVAL },
	14: { period: CURRENT_MONTH, interval: WEEKLY_INTERVAL },
	0: defaultInterval,
}

export const getMaxDifferentRange = (props: {
	currentTo: string
	currentFrom: string
	previousTo: string
	previousFrom: string
}) => {
	const differencesInDaysCurrent = getDifferentRange(
		props.currentFrom,
		props.currentTo
	)
	const differencesInDaysPrevious = getDifferentRange(
		props.previousFrom,
		props.previousTo
	)

	return differencesInDaysCurrent > differencesInDaysPrevious
		? differencesInDaysCurrent
		: differencesInDaysPrevious
}

export const getPeriodFromDate = (filtersBenchmark: any) => {
	const differenceInDays = getMaxDifferentRange(filtersBenchmark)

	const [, period = defaultInterval] =
		Object.entries(limitCustomRanges)
			.reverse()
			.find(([limit]) => differenceInDays >= Number(limit)) || []

	return period
}

export const translatePresetToRange = (filtersBenchmark: any) => {
	const { period } = filtersBenchmark

	const range = TRANSLATION_PRESETS_RANGES[period]

	if (!range) {
		return getPeriodFromDate(filtersBenchmark)
	}

	return { period: range, interval: "" }
}

export const isIn = (value: string, values: string[]) =>
	values.some((v) => v === value)

export const getFormattedDateRange = (
	benchmarkPeriods: BenchmarkPeriod,
	benchmarkPeriod: string,
	period: string,
	intl: IntlShape
) => {
	const label = intl.formatMessage({
		id: validateIsPreviousPeriod(period)
			? getNameToPeriodLegend(benchmarkPeriod)?.previous
			: getNameToPeriodLegend(benchmarkPeriod).current,
	})

	const date = mapTimeInterval(benchmarkPeriods, period, intl)

	return `${label}: ${date}`
}

export const validateFiltersForBenchmark = (filters: RequestParameters) => {
	return [
		filters.currentFrom,
		filters.currentTo,
		filters.previousFrom,
		filters.previousTo,
	].every(isNotEmpty)
}

export const getVariantPrevious = (flag: boolean) => {
	return flag ? "current" : "previous"
}

export const getVariantCurrent = (flag: boolean) => {
	return flag ? "previous" : "current"
}

export const getSubtitleByFilters = (
	filterLevelSelected: FilterLevelSelected = {
		filterLevelSelected: "",
		selectedFilters: [
			{
				code: "",
				name: "",
			},
		],
	},
	intl: IntlShape
): string => {
	const departament_title = intl.formatMessage({
		id: DEPARTMENT_MARKET_TITLE_MULTIPLE,
	})
	const category_title = intl.formatMessage({
		id: CATEGORIES_MARKET_TITLE_MULTIPLE,
	})
	const subcategory_title = intl.formatMessage({
		id: SUB_CATEGORIES_MARKET_TITLE_MULTIPLE,
	})

	const levelSelected = filterLevelSelected.filterLevelSelected
	const specificFilters = Object.keys(filterLevelSelected.selectedFilters)
		.length
	const selectedFiltersLocal = filterLevelSelected.selectedFilters

	switch (levelSelected) {
		case "subcategories":
			return getLevelSelected(
				specificFilters,
				subcategory_title,
				category_title,
				selectedFiltersLocal
			)
		case "categories":
			return getLevelSelected(
				specificFilters,
				category_title,
				departament_title,
				selectedFiltersLocal
			)
		default:
			return getLevelSelected(
				specificFilters,
				departament_title,
				departament_title,
				selectedFiltersLocal
			)
	}
}

const getLevelSelected = (
	specificFilters: number,
	value: string,
	alterValue: string,
	selectedFiltersLocal: Array<Tuple>
): string => {
	if (specificFilters > 1) {
		return value
	} else if (specificFilters === 1) {
		return selectedFiltersLocal[0].name
	}
	return alterValue
}

export const getLevelName = (
	filtersBenchmark: any,
	levelName: LevelName
): LevelName => {
	return levelName === FORMATS && filtersBenchmark.formats.length
		? STORE
		: levelName
}

export const canShowCustomerInfo = (arr: Array<boolean>) =>
	arr.reduce((curr, next) => curr || next)
